import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Handshake, Scroll, Users, Heart, MapPin, CheckCircle, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const PSPTrainingHubPage = () => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState(null);

  const sections = [
    {
      id: 'land-acknowledgment',
      emoji: '🌍',
      title: 'Land Acknowledgment',
      subtitle: 'Honoring Indigenous Peoples',
      icon: MapPin,
      color: 'from-emerald-500 to-teal-500',
      content: {
        intro: "We acknowledge that we gather on the traditional, unceded territories of Indigenous peoples across Turtle Island (North America). We recognize the ongoing impacts of colonization on Indigenous communities.",
        sections: [
          {
            title: 'Why Land Acknowledgments Matter',
            text: 'Land acknowledgments are a way to recognize Indigenous peoples as the original stewards of this land. They help us understand the historical and ongoing relationship between Indigenous peoples and their territories.',
            items: [
              'Recognize Indigenous peoples as the original inhabitants',
              'Acknowledge the impact of colonization',
              'Commit to learning and supporting Indigenous rights',
              'Create space for reconciliation and healing'
            ]
          },
          {
            title: 'In Our Work',
            text: 'As harm reduction workers, we recognize that colonization has contributed to the health inequities faced by Indigenous communities today. We commit to:',
            items: [
              'Centering Indigenous voices and experiences',
              'Supporting Indigenous-led harm reduction initiatives',
              'Learning about the impacts of intergenerational trauma',
              'Advocating for culturally safe services'
            ]
          }
        ],
        footer: 'We honor the land, water, and all living beings. We commit to walking in a good way.'
      }
    },
    {
      id: 'facilitator-guide',
      emoji: '📋',
      title: 'Facilitator Scripts',
      subtitle: 'Running PSP Workshops',
      icon: GraduationCap,
      color: 'from-purple-500 to-violet-500',
      content: {
        intro: "These scripts help facilitators lead PSP workshops with confidence. Use them as a starting point and adapt to your community's needs.",
        sections: [
          {
            title: 'Opening the Space',
            subtitle: 'Setting the tone',
            items: [
              '"Welcome everyone. This is a judgment-free space."',
              '"What\'s shared here stays here. Confidentiality is key."',
              '"There are no wrong questions or experiences."',
              '"You can step out anytime if you need a break."',
              '"We\'re here to learn from each other, not to lecture."'
            ]
          },
          {
            title: 'Introducing Harm Reduction',
            items: [
              '"Harm reduction means meeting people where they\'re at."',
              '"It\'s not about stopping drug use, it\'s about staying safe while using."',
              '"Everyone deserves care regardless of their choices."',
              '"Small changes can make a big difference in safety."'
            ]
          },
          {
            title: 'Facilitating Discussions',
            subtitle: 'Keeping it flowing',
            items: [
              'Ask open-ended questions: "What has your experience been?"',
              'Validate contributions: "Thank you for sharing that."',
              'Redirect gently: "That\'s important. Let\'s also hear from others."',
              'Manage time: "We have a few more minutes on this topic."',
              'Check in: "How is everyone feeling about this conversation?"'
            ]
          },
          {
            title: 'Handling Difficult Moments',
            items: [
              'If someone is triggered: "Let\'s take a pause. Would you like to step out?"',
              'If there\'s conflict: "Both perspectives are valid. Let\'s find common ground."',
              'If someone dominates: "I appreciate your input. Let\'s hear from others."',
              'If it gets too heavy: "This is hard stuff. Let\'s take a breath together."'
            ]
          },
          {
            title: 'Closing the Workshop',
            items: [
              '"Thank you all for being present and vulnerable."',
              '"Remember, what\'s shared here stays here."',
              '"Take care of yourselves tonight and reach out if needed."',
              '"Resources are available - take what you need."',
              '"You are valued. Your safety matters."'
            ]
          }
        ]
      }
    },
    {
      id: 'code-of-conduct',
      emoji: '🤝',
      title: 'Code of Conduct',
      subtitle: 'Community Agreements',
      icon: Handshake,
      color: 'from-pink-500 to-rose-500',
      content: {
        intro: "These agreements create safer spaces for everyone. They apply to all PSP events, workshops, and community gatherings.",
        sections: [
          {
            title: 'Core Agreements',
            items: [
              'Respect everyone\'s identity, pronouns, and boundaries',
              'Keep what\'s shared confidential',
              'Listen actively and don\'t interrupt',
              'Speak from your own experience ("I" statements)',
              'Make space for others to share',
              'No judgment about drug use or sexual practices'
            ]
          },
          {
            title: 'Consent Culture',
            items: [
              'Ask before touching, hugging, or physical contact',
              'Respect when someone says "no" or sets a boundary',
              'Check in before sharing potentially triggering content',
              'Understand that consent can be withdrawn at any time',
              'Recognize power dynamics and privilege'
            ]
          },
          {
            title: 'Anti-Oppression',
            text: 'We actively work against all forms of discrimination:',
            items: [
              'No racism, homophobia, transphobia, or ableism',
              'No stigmatizing language about drug use or HIV status',
              'No body shaming or appearance-based comments',
              'Challenge harmful stereotypes when you see them',
              'Center the voices of those most impacted'
            ]
          },
          {
            title: 'If Something Happens',
            items: [
              'Speak to a facilitator or organizer privately',
              'Your concerns will be taken seriously',
              'We prioritize the safety of those harmed',
              'Accountability processes focus on repair, not punishment',
              'Support is available for everyone involved'
            ]
          }
        ],
        footer: 'We create the community we want to see. Together, we keep each other safe.'
      }
    },
    {
      id: 'peer-support',
      emoji: '💜',
      title: 'Peer Support Basics',
      subtitle: 'Supporting Each Other',
      icon: Heart,
      color: 'from-cyan-500 to-blue-500',
      content: {
        intro: "Peer support is about people with lived experience supporting others. It's not about being an expert - it's about being real and caring.",
        sections: [
          {
            title: 'What Peer Support Is',
            items: [
              'Sharing your own experiences when helpful',
              'Listening without judgment or advice-giving',
              'Offering practical help and information',
              'Being present and consistent',
              'Respecting autonomy and choices'
            ]
          },
          {
            title: 'What Peer Support Is NOT',
            items: [
              'Not therapy or counseling',
              'Not telling people what to do',
              'Not fixing or saving people',
              'Not taking on someone else\'s problems',
              'Not having all the answers'
            ]
          },
          {
            title: 'Active Listening Skills',
            items: [
              'Give your full attention - put away distractions',
              'Use body language that shows you\'re engaged',
              'Reflect back what you hear: "It sounds like..."',
              'Ask clarifying questions',
              'Validate feelings: "That makes sense that you\'d feel that way"'
            ]
          },
          {
            title: 'Setting Boundaries',
            text: 'Taking care of yourself is essential for supporting others:',
            items: [
              'It\'s okay to not be available 24/7',
              'You can end conversations that are too heavy',
              'Refer to professionals when needed',
              'Process your own feelings separately',
              'Take breaks and practice self-care'
            ]
          }
        ],
        footer: 'You don\'t have to have it all together to help someone. Just be real.'
      }
    },
    {
      id: 'workshop-activities',
      emoji: '🎯',
      title: 'Workshop Activities',
      subtitle: 'Interactive Learning',
      icon: Users,
      color: 'from-orange-500 to-amber-500',
      content: {
        intro: "These activities help create engaging, interactive workshops. Adapt them to your group's needs and comfort levels.",
        sections: [
          {
            title: 'Icebreakers',
            subtitle: '5-10 minutes',
            items: [
              'Name + pronoun + one word for how you\'re feeling today',
              'Two truths and a wish (something you hope for)',
              'If you were a harm reduction supply, what would you be?',
              'Rose/Thorn/Bud: Something good, challenging, and you\'re looking forward to'
            ]
          },
          {
            title: 'Myth vs. Fact',
            subtitle: '15-20 minutes',
            text: 'Read statements about drugs, HIV, or harm reduction. Participants vote "myth" or "fact" then discuss.',
            items: [
              'Helps identify misconceptions',
              'Creates space for questions',
              'Builds shared knowledge',
              'Can be done anonymously with apps/cards'
            ]
          },
          {
            title: 'Scenario Role-Plays',
            subtitle: '20-30 minutes',
            text: 'Small groups act out scenarios like helping a friend who\'s overamping or negotiating safer sex.',
            items: [
              'Builds practical skills',
              'Safe space to practice conversations',
              'Learn from each other\'s approaches',
              'Debrief after each scenario'
            ]
          },
          {
            title: 'Resource Mapping',
            subtitle: '15 minutes',
            text: 'Group creates a visual map of local resources: where to get testing, supplies, support.',
            items: [
              'Shares community knowledge',
              'Identifies gaps in services',
              'Creates tangible takeaway',
              'Updates existing resource lists'
            ]
          },
          {
            title: 'Closing Circle',
            subtitle: '5-10 minutes',
            items: [
              'One thing you learned today',
              'One thing you\'ll do differently',
              'One word to describe how you\'re leaving',
              'Appreciation for the group'
            ]
          }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedSection ? setSelectedSection(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">🎓</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedSection ? sections.find(s => s.id === selectedSection)?.title : 'PSP Training Hub'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedSection ? (
          <>
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-2xl font-bold mb-3">Facilitator Training Hub</h2>
              <p className="text-white/90 leading-relaxed">
                Resources for running PSP workshops, training peer supporters, and building safer community spaces. 💜
              </p>
            </div>

            <div className="space-y-4">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    data-testid={`section-${section.id}`}
                    className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${section.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    <div className="relative z-10 flex items-start">
                      <div className="text-5xl mr-4 emoji-bounce">{section.emoji}</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                          {section.title}
                        </h3>
                        <p className="text-sm text-[rgb(var(--text-secondary))]">
                          {section.subtitle}
                        </p>
                      </div>
                      <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Quick tip */}
            <div className="mt-8 p-4 bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 rounded-2xl">
              <div className="flex items-start">
                <Sparkle size={20} weight="duotone" className="text-[rgb(var(--primary))] mr-3 mt-0.5" />
                <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">
                  <strong className="text-[rgb(var(--text-primary))]">Pro tip:</strong> Start with the Code of Conduct before any workshop to set clear community agreements.
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {sections.find(s => s.id === selectedSection)?.content && (() => {
              const section = sections.find(s => s.id === selectedSection);
              const content = section.content;
              
              return (
                <>
                  {content.intro && (
                    <div className={`p-6 bg-gradient-to-br ${section.color} rounded-3xl text-white`}>
                      <p className="leading-relaxed text-lg">
                        {content.intro}
                      </p>
                    </div>
                  )}

                  {content.sections && content.sections.map((sect, idx) => (
                    <div key={idx} className="space-y-3">
                      <h3 className="text-xl font-bold text-[rgb(var(--text-primary))]">
                        {sect.title}
                      </h3>
                      {sect.subtitle && (
                        <p className="text-[rgb(var(--text-secondary))] font-semibold text-sm">
                          {sect.subtitle}
                        </p>
                      )}
                      {sect.text && (
                        <p className="text-[rgb(var(--text-secondary))] leading-relaxed">
                          {sect.text}
                        </p>
                      )}
                      {sect.items && (
                        <ul className="space-y-2">
                          {sect.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start p-3 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
                              <CheckCircle size={18} weight="duotone" className="text-[rgb(var(--primary))] mr-3 mt-0.5 flex-shrink-0" />
                              <span className="text-[rgb(var(--text-secondary))] leading-relaxed">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  {content.footer && (
                    <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
                      <p className="text-[rgb(var(--text-primary))] font-bold">
                        {content.footer}
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default PSPTrainingHubPage;
